import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { billingApi, creditsApi } from "api";
import cn from "classnames";
import type { UseCredits } from "hooks/useCredits";
import { isUnauthenticated } from "utils/error.utils";
import { formatCount } from "utils/number.utils";
import { billingView, cardLabel, clampCredits, formatCredits, formatUsd, qcShort, totalUsd } from "./credits.model";
import { QCreditLogo } from "./QCreditLogo";

type HeaderCreditsProps = {
  credits: UseCredits;
  onOpenBilling: () => void;
  onSessionLost: () => void;
};

/** The class the host node carries; the cabinet's stylesheet styles the pill through it. */
export const HEADER_HOST_CLASSNAME = "cabinet-app-header";

/** The amount the quick buy opens on, as the design does. */
const QUICK_DEFAULT = 250;

/**
 * Finds, or makes, the node the pill renders into: the site header's own
 * button row, just before the account button. The header belongs to the host
 * page; the cabinet only borrows a slot in it while the cabinet is open.
 */
const useHeaderHost = (): HTMLElement | null => {
  const [host, setHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const row = document.querySelector<HTMLElement>(".header__wrapper-buttons");

    if (row === null) return undefined;

    const node = document.createElement("div");
    node.className = HEADER_HOST_CLASSNAME;
    row.insertBefore(node, row.querySelector("#header-user-toggle"));
    setHost(node);

    return () => node.remove();
  }, []);

  return host;
};

/**
 * `#hdr-qc`: the QCredits balance next to the account button, and the quick
 * buy behind it. Buying goes straight to Stripe Checkout; anything more than a
 * one-off pack is a click away in Plan & Billing.
 */
export const HeaderCredits = ({ credits, onOpenBilling, onSessionLost }: HeaderCreditsProps) => {
  const host = useHeaderHost();
  const { state, pricing } = credits;
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(QUICK_DEFAULT);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Click outside or Escape closes the popover, as on the design.
  useEffect(() => {
    if (!open) return undefined;

    const onClick = (event: MouseEvent) => {
      if (wrapRef.current !== null && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (host === null || state === null || pricing === null) return null;

  const view = billingView(state);
  const balance = view.empty ? state.balance.free : state.balance.total;
  const failing = view.auto?.status === "failed" ? view.auto.topUp : null;
  const valid = amount >= pricing.min_credits;

  const buy = async () => {
    setBusy(true);

    try {
      const { data } = await creditsApi.checkout({ credits: clampCredits(amount, pricing) });

      window.location.assign(data.url);
    } catch (caught: unknown) {
      if (isUnauthenticated(caught)) onSessionLost();
      setBusy(false);
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await billingApi.portal();

      window.location.assign(data.url);
    } catch (caught: unknown) {
      if (isUnauthenticated(caught)) onSessionLost();
    }
  };

  return createPortal(
    <div className="hdr-qcw" ref={wrapRef}>
      <button
        type="button"
        className="hdr-qc"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`QCredits balance: ${formatCount(balance)}`}
        onClick={() => setOpen(!open)}
      >
        <span className="hdr-qc__in">
          <b>{qcShort(balance)}</b>
          <QCreditLogo className="hdr-qc__lg" />
        </span>
      </button>

      {open && (
        <div className="hqc" role="dialog" aria-label="Buy extra QCredits">
          <div className="hqc__h">Buy extra QCredits</div>
          <div className="hqc__pks">
            {pricing.packs.map((pack) => (
              <button
                key={pack.credits}
                type="button"
                className={cn("hqc__pk", { "is-on": pack.credits === amount })}
                onClick={() => setAmount(pack.credits)}
              >
                <b>{formatCount(pack.credits)}</b>
                <span>${formatCount(pack.total_usd)}</span>
              </button>
            ))}
          </div>
          <label className="hqc__in">
            <span>Amount</span>
            <input
              type="number"
              min={pricing.min_credits}
              max={pricing.max_credits}
              step={1}
              inputMode="numeric"
              value={amount === 0 ? "" : amount}
              onChange={(event) =>
                setAmount(Math.max(0, Math.min(pricing.max_credits, Math.floor(Number(event.target.value) || 0))))
              }
            />
          </label>
          <div className="hqc__sum">
            <div>
              <b>{formatCredits(amount)}</b>
              <span>{valid ? formatUsd(totalUsd(pricing, amount)) : "-"}</span>
            </div>
            <button type="button" className="pcbtn pcbtn--main" disabled={busy || !valid} onClick={buy}>
              {busy ? "Opening…" : "Buy"}
            </button>
          </div>
          <button
            type="button"
            className="hqc__more"
            onClick={() => {
              setOpen(false);
              onOpenBilling();
            }}
          >
            More options in Plan &amp; Billing
          </button>
          {failing !== null && (
            <div className="hqc__fail">
              <div className="hqc__fail-top">
                <span className="acr__fail-i" aria-hidden="true">
                  !
                </span>
                <div>
                  <b>Auto top-up payment failed</b>
                  <p>
                    {cardLabel(failing.card) ?? "Your card"} was declined for {formatCredits(failing.credits)} (
                    {formatUsd(failing.amount_usd)}).
                  </p>
                  <button type="button" className="hqc__fail-a" onClick={openPortal}>
                    Update card
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>,
    host
  );
};
